import type { ResponseConstruction } from '@/app/services/cognition/cognition-service';

// Per-model synthesis cards for stage 4 (ported from the old run-output stage-4
// body so behavior is preserved): strengths / weaknesses / reasoning per model.
export function SynthesisCards({
  rows,
  modelLabel,
}: {
  rows: ResponseConstruction[];
  modelLabel: (id: string) => string;
}) {
  if (!rows?.length) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {rows.map((r, idx) => {
        // Backend sends a single strength/weakness/narrative per model (the
        // placeholder "—" means "none"). Map them into the bullet-list UI.
        const isEmpty = (s: unknown) => {
          const t = String(s ?? '').trim();
          return !t || t === '—';
        };
        const strengths: string[] = isEmpty(r?.topStrength) ? [] : [String(r.topStrength).trim()];
        const weaknesses: string[] = isEmpty(r?.keyWeakness) ? [] : [String(r.keyWeakness).trim()];
        const reasoningText = isEmpty(r?.narrative) ? '' : String(r.narrative).trim();

        return (
          <div
            key={`${r.model}-${idx}`}
            style={{
              padding: 18,
              borderRadius: 12,
              background: 'var(--card)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--foreground)', margin: 0 }}>
                {modelLabel(String(r.model || '')) || String(r.model || '—')}
              </h4>
              <span
                style={{
                  padding: '3px 11px',
                  borderRadius: 99,
                  background: 'var(--success-bg)',
                  color: 'var(--success-text)',
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {r.confidence}% confidence
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              <SynthList title="Strengths" items={strengths} dot="var(--success-text)" />
              <SynthList title="Weaknesses" items={weaknesses} dot="var(--infrared-coral)" />
            </div>

            {reasoningText && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 10,
                  background: 'var(--signal-blue-10)',
                  border: '1px solid var(--signal-blue-20)',
                }}
              >
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--foreground)', margin: '0 0 4px' }}>Reasoning</p>
                <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--muted-foreground)', margin: 0, overflowWrap: 'anywhere' }}>
                  {reasoningText}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SynthList({ title, items, dot }: { title: string; items: string[]; dot: string }) {
  return (
    <div>
      <h5 style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)', margin: '0 0 10px' }}>{title}</h5>
      {items.length ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((p, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, marginTop: 7, background: dot, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--foreground)', overflowWrap: 'anywhere' }}>{p}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>—</div>
      )}
    </div>
  );
}
